import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { MatSort, Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AdvancedSearchService } from 'src/app/modules/pos/services/advanced-search.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { LoadingState } from 'core/common-entities/loading-state.enum';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { LeadsService } from '../../../leads/leads.service';
import { Account } from '../../entities/account-entity.class';
import { AccountsListService } from '../../services/accounts-list.service';
import { AccountsEmailComponent } from '../accounts-email/accounts-email.component';
import { DeliveryEmailComponent } from '../delivery-email/delivery-email.component';
import { MergeLeadModalComponent } from '../merge-lead-modal/merge-lead-modal.component';
import { LeadsRejectComponent } from '../../../leads/merge-leads/leads-reject/leads-reject.component';

@Component({
  selector: 'app-accounts-list-table',
  styleUrls: ['accounts-list-table.component.scss'],
  templateUrl: 'accounts-list-table.component.html',
  standalone: false
})

export class AccountsListTableComponent implements OnInit, OnDestroy {

  constructor(
    private accountsListService: AccountsListService,
    private appInfoService: AppInfoService,
    private leadsService: LeadsService,
    private router: Router,
    private dialog: MatDialog,
    private appStateService: AppStateService,
    private advancedSearchService: AdvancedSearchService,
    private deviceService: DeviceDetectorService,
    private notificationService: NotificationService
  ) { }

  subs = new Subscription();
  appStrings = this.appInfoService.appStrings;
  inactiveLnStatus = 3;
  leadDialogWidth = '370px';
  deliveryDialogWidth = '470px';
  currentPage = 0;
  pageOffset = 0;
  showPagination = true;
  accountType = 1;
  iconMapping = {
    1: 'group',
    2: 'how_to_reg',
    3: 'local_shipping'
  };
  displayedColumns: string[] = [
    'icon',
    'accountManagerName',
    'name1',
    'name2',
    'childName1',
    'priceMatrix',
    'business',
    'address',
    'actions'
  ];
  recordList$: Subject<Account[]>;
  totalRecords = 0;
  accountsPerPage = this.accountsListService.pageSize;
  totalPages = 0;
  loading = true;
  loadingPartial = false;
  isOnline: boolean;
  isAdmin: boolean;
  currentUserId: string;
  mergeLeadDialogWidth = '500px';
  currentPageItemCount = 0;

  @ViewChild(MatSort) tableSort: MatSort;

  ngOnInit() {
    this.recordList$ = this.accountsListService.accountList$;
    this.isOnline = this.appStateService.state.isOnline;
    this.isAdmin = this.appInfoService.isAdminTypeUser();
    this.currentUserId = this.appInfoService.userData?.UserInfo?.EmployeeId;

    this.subs.add(this.accountsListService.isProcessing$.subscribe(isLoading => {
      this.loading = isLoading === LoadingState.loading;
      this.loadingPartial = isLoading === LoadingState.partiallyLoaded;
    }));
    this.subs.add(this.accountsListService.totalRecords$.subscribe(value => {
      const newTotalPages = value ? Math.ceil(value / this.accountsPerPage) : 0;
      // If new search has fewer pages, reset pagination component
      if (newTotalPages < this.totalPages && newTotalPages > 0) {
        this.showPagination = false;
        this.pageOffset = 0;
        setTimeout(() => this.showPagination = true, 0);
      }
      this.totalRecords = value;
      this.totalPages = newTotalPages;
    }));
    this.subs.add(this.accountsListService.currentPage$.subscribe(value => this.currentPage = value - 1));
    this.subs.add(this.recordList$.subscribe(records => {
      this.currentPageItemCount = records?.length ?? 0;
    }));
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  loadPage(event: PaginationPageEvent) {
    this.currentPage = event.pageNumber - 1;
    this.accountsListService.updatePage(event.pageNumber);
    this.pageOffset = (event.pageNumber - 1) * this.accountsPerPage;

    if (this.deviceService.isMobile()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  viewAccountDetails(customer) {
    this.navigateTo(customer)();
  }

  navigateTo(customer) {
    const toCall = {
      1: () => { // Account
        const customerId = customer.customerId;
        this.router.navigate(['accounts', customerId]);
      },
      2: () => { // Lead
        const leadId = customer.leadId;
        this.router.navigate(['/accounts-leads', leadId]);
      },
      3: () => { // New Delivery Location
        const leadId = customer.leadId;
        this.router.navigate(['leads/delivery-location/edit', leadId]);
      }
    };
    return toCall[customer.recordType];
  }

  openAccountsEmailPop(record, event: Event) {
    event.stopPropagation();
    const dialog = this.dialog.open(AccountsEmailComponent, {
      width: this.leadDialogWidth,
      data: {
        record
      }
    });

    dialog.afterClosed().subscribe(result => {
      if (result) {
        this.leadsService.getLeadDetails(record.leadId, resultDetails =>
          this.updateRecordStatus(record, resultDetails));
      }
    });
  }

  openDeliveryEmailPop(record, event: Event) {
    event.stopPropagation();
    const dialog = this.dialog.open(DeliveryEmailComponent, {
      width: this.deliveryDialogWidth,
      data: {
        record
      }
    });
    dialog.afterClosed().subscribe(result => {
      if (result) {
        this.leadsService.getDeliveryLocationDetails(record.leadId, resultDelivery => {
          this.updateRecordStatus(record, resultDelivery);
        });
      }
    });
  }

  openRejectLeadModal(record, event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(LeadsRejectComponent, {
      width: this.leadDialogWidth,
      data: { leadId: record.leadId }
    });
    dialogRef.componentInstance.onRejectLead.subscribe(reason => {
      this.leadsService.rejectLead(record.leadId, reason).subscribe(() => {
        this.accountsListService.getAccountList(null);
        this.notificationService.showNotification(this.appStrings.leads_rejectSuccess, NotificationType.Success);
      });
    });
  }

  openMergeLeadModal(record, event: Event) {
    event.stopPropagation();
    const dialog = this.dialog.open(MergeLeadModalComponent, {
      width: this.deviceService.isMobile() ? '' : this.mergeLeadDialogWidth,
      panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      data: { record }
    });
    dialog.afterClosed().subscribe(result => {
      if (result?.merged) {
        this.accountsListService.getAccountList(null);
      }
    });
  }

  openNewQuote(record, event: Event) {
    event.stopPropagation();
    this.router.navigate(this.getNewQuoteRoute(record));
  }

  private getNewQuoteRoute(record): any[] {
    if (record.recordType === 1) {
      return ['/quotes', record.customerId, 'new'];
    }

    if (record.recordType === 2) {
      return ['/quotes/lead/', record.leadId, 'new'];
    }

    return ['/quotes', 'new'];
  }

  private updateRecordStatus(record, result) {
    record.leadStatus = result.leadStatus;
    this.accountsListService.setLnStatusLabel(record);
  }

  sortData(sort: Sort) {
    if (!sort.active) {
      // reset sorting due to new search, no action needed
      return;
    } else if (!sort.direction) {
      // sorting is reset due to clicking three times on sort arrow
      sort = null;
      this.accountsListService.resetSort();
    }

    this.resetPage();
    this.accountsListService.getAccountList(null, sort);
  }

  resetSortIndicator(sort?) {
    this.advancedSearchService.resetSort(this.tableSort, sort);
  }

  resetPage() {
    this.currentPage = 0;
  }

  isOwnLead(record): boolean {
    return record.employeeId === this.currentUserId;
  }

  displayNewQuoteButton(record): boolean {
    return record.recordType === 1 || record.recordType === 2;
  }

  navigateToB2BUsers(customer, event: Event) {
    event.stopPropagation();
    const customerId = customer.customerId;
    this.router.navigate(['/accounts', customerId, { tab: 'b2b-users' }]);
  }
}
